import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { observer } from 'mobx-react-lite'

// evertel
import { useService } from '@evertel/di'
import { Api } from '@evertel/api'
import { SessionService } from '@evertel/session/SessionService'
import { Analytics } from '@evertel/analytics'
import { CurrentUserController, BlueUserStore } from '@evertel/blue-user'
import { DepartmentController } from '@evertel/department'
import { IntegrationController } from '@evertel/integration'
import { mediaMultipartUpload } from '@evertel/media'
import { fileToUploadableMedia, formatBytes } from '@evertel/utils'
import { ProfilePic } from '@evertel/web/user'
import { DepartmentAvatar } from '@evertel/web/department'
import { useUI } from '../UIContext'
import { Button } from './Button'
import { Icon } from './Icon'
import { Image } from './Image'
import { Spinner } from './Spinner'


export interface ProfilePicUploaderHandle {
    upload: () => Promise<void>
}

interface ProfilePicUploaderProps {
    id: number
    type?: 'department' | 'user' | 'integration' | 'documentSchema'
    initialPic?: string
    /** When true, selecting a file shows a local preview but does not upload until upload() is called via ref */
    deferred?: boolean
    /** Rejects files larger than this before they're staged or uploaded */
    maxFileSizeBytes?: number
    /** Accepted mime types. Enforced on both the file dialog and drag-and-drop */
    acceptedTypes?: string[]
    /** Hands the picked (or cleared) file to the parent, for callers that own the upload themselves */
    onFileSelected?: (file: File|null) => void
    onUploadStart?: () => void
    onUploadEnd?: () => void
}

const DEFAULT_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

// generous enough for a high-res phone camera photo, low enough to keep a
// pathological upload from being stored and processed
const DEFAULT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

const ProfilePicUploader = observer(forwardRef<ProfilePicUploaderHandle, ProfilePicUploaderProps>(({
    id,
    type = 'user',
    initialPic,
    deferred = false,
    maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE_BYTES,
    acceptedTypes = DEFAULT_ACCEPTED_TYPES,
    onFileSelected,
    onUploadStart,
    onUploadEnd
}, ref) => {

    const api = useService(Api, [])
    const sessionService = useService(SessionService, [])
    const analytics = useService(Analytics, [])
    const currentUserController = useService(CurrentUserController, [])
    const departmentController = useService(DepartmentController, [])
    const integrationController = useService(IntegrationController, [])
    const userStore = useService(BlueUserStore, [])

    const { addToast } = useUI()

    // For integration type, resolve the integrationUserId and derive the pic from BlueUserStore
    const integration = type === 'integration' ? integrationController.getIntegrationById(id) : undefined
    const integrationUserId = integration?.integrationUserId
    const resolvedInitialPic = type === 'integration'
        ? (integrationUserId ? userStore.findById(integrationUserId)?.publicImage : undefined)
        : initialPic

    const [profilePic, setProfilePic] = useState<string | null>(resolvedInitialPic || null)
    const [stagedFile, setStagedFile] = useState<File | null>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)

    const fileInputRef = useRef<HTMLInputElement>(null)
    const previewUrlRef = useRef<string | null>(null)
    const [isDragOver, setIsDragOver] = useState(false)

    useEffect(() => {
        setProfilePic(resolvedInitialPic || null)
    }, [resolvedInitialPic])

    // Revoke any pending object URL on unmount to avoid memory leaks
    useEffect(() => {
        return () => {
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
        }
    }, [])

    useImperativeHandle(ref, () => ({
        upload: async () => {
            if (!stagedFile) return
            await uploadFile(stagedFile)
            if (previewUrlRef.current) {
                URL.revokeObjectURL(previewUrlRef.current)
                previewUrlRef.current = null
            }
            setStagedFile(null)
        }
    }))

    const uploadFile = async (file: File) => {
        if (!file || !file.type.startsWith('image/')) return

        try {
            setIsUploading(true)
            setUploadProgress(0)
            onUploadStart?.()

            const modelName = type === 'department' ? 'Departments' : type === 'integration' ? 'Integrations' : 'BlueUsers'

            const uploadableMedia = await fileToUploadableMedia(file)

            const result = await mediaMultipartUpload(api, {
                ...uploadableMedia,
                meta: uploadableMedia.meta || {},
                onProgress: (completed, total) => {
                    setUploadProgress(Math.round((completed / total) * 100))
                },
                modelName,
                modelId: id,
                userId: sessionService.currentUserId,
                deviceId: 0
            })

            // refresh store data
            if (type === 'user') {
                currentUserController.updateLocalStore({
                    publicMedia: result,
                    publicMediaId: result.id,
                    publicImage: result.url
                })
            } else if (type === 'department') {
                await departmentController.refreshDepartment(id)
            } else if (type === 'integration') {
                if (integrationUserId) {
                    userStore.update({
                        id: integrationUserId,
                        publicMedia: result,
                        publicMediaId: result.id,
                        publicImage: result.url
                    })
                }
            }

            setProfilePic(result?.url || resolvedInitialPic || null)

            analytics.logEvent('User', type === 'user' ? 'edit_profile_image' : type === 'integration' ? 'edit_connector_image' : 'edit_department_image')

            onUploadEnd?.()
        } catch (error: any) {
            console.error('ProfilePicUploader upload error:', error?.message || error)
        } finally {
            setIsUploading(false)
            setUploadProgress(0)
        }
    }

    const handleFile = (file: File) => {
        if (!file || !file.type.startsWith('image/')) return

        // the input's `accept` only filters the file dialog; drag-and-drop bypasses it
        if (!acceptedTypes.includes(file.type)) {
            const friendlyTypes = acceptedTypes.map(t => t.replace('image/', '').replace('jpeg', 'jpg').toUpperCase())
            addToast({
                title: 'Unsupported image type',
                color: 'danger',
                message: `Please choose a ${friendlyTypes.join(', ')} image.`
            })
            return
        }

        if (maxFileSizeBytes && file.size > maxFileSizeBytes) {
            addToast({
                title: 'Image too large',
                color: 'danger',
                message: `That image is ${formatBytes(file.size, 1)}. Please choose one under ${formatBytes(maxFileSizeBytes, 0)}.`
            })
            return
        }

        if (deferred) {
            // Show a local preview without uploading — upload() must be called externally (e.g. on Save)
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
            const url = URL.createObjectURL(file)
            previewUrlRef.current = url
            setProfilePic(url)
            setStagedFile(file)
            onFileSelected?.(file)
        } else {
            uploadFile(file)
        }
    }

    const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        // reset input so same file can be re-selected
        e.target.value = ''

        handleFile(file)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(false)

        const file = e.dataTransfer.files?.[0]
        if (file) handleFile(file)
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(true)
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(false)
    }

    const handleDeletePic = async () => {
        try {
            if (type === 'user') {
                await currentUserController.deleteProfilePic()
                analytics.logEvent('User', 'edited_profile_image')
            }

            // Revoke any staged preview object URL before clearing
            if (previewUrlRef.current) {
                URL.revokeObjectURL(previewUrlRef.current)
                previewUrlRef.current = null
            }
            setStagedFile(null)
            setProfilePic(null)
            onFileSelected?.(null)
        } catch (error: any) {
            console.error('ProfilePicUploader delete error:', error?.message || error)
        }
    }

    if (isUploading) {
        return (
            <div className="mb-3 position-relative" style={{ width: 150, height: 150 }}>
                <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{
                        width: 150,
                        height: 150,
                        borderRadius: '20%',
                        backgroundColor: 'rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <Spinner />
                    {uploadProgress > 0 && (
                        <small className="mt-1 text-muted">{uploadProgress}%</small>
                    )}
                </div>
            </div>
        )
    }

    if (profilePic) {
        return (
            <div className="mb-3 position-relative">
                {type === 'documentSchema' ? (
                    // no avatar component to defer to — the id may not exist yet
                    <Image
                        url={profilePic}
                        resizeMode="contain"
                        width={150}
                        height={150}
                    />
                ) : type === 'department' ? (
                    <DepartmentAvatar
                        departmentId={id}
                        width={150}
                        height={150}
                    />
                ) : type === 'integration' ? (
                    <ProfilePic
                        userId={integrationUserId}
                        imageUrl={profilePic}
                        width={150}
                        height={150}
                    />
                ) : (
                    <ProfilePic
                        userId={id}
                        imageUrl={profilePic}
                        width={150}
                        height={150}
                    />
                )}
                <Button
                    color="danger"
                    size="sm"
                    className="position-absolute top-0 start-0"
                    onClick={handleDeletePic}
                >
                    <Icon
                        name="trash"
                        className="text-white"
                    />
                </Button>
            </div>
        )
    }

    // No pic — show upload area
    return (
        <div className="mb-3">
            <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{
                    width: 150,
                    height: 150,
                    border: `2px dashed ${isDragOver ? 'var(--bs-primary, #0d6efd)' : 'var(--bs-border-color, #dee2e6)'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: isDragOver ? 'var(--bs-primary-bg-subtle, #e7f1ff)' : 'var(--bs-tertiary-bg, #f8f9fa)',
                    transition: 'border-color 0.15s, background-color 0.15s'
                }}
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        fileInputRef.current?.click()
                    }
                }}
                aria-label={`Upload ${type === 'department' ? 'agency' : type === 'integration' ? 'connector' : type === 'documentSchema' ? 'template' : 'profile'} picture`}
            >
                <Icon name="cloud-arrow-up" className="mb-2 text-muted" />
                <small className="text-muted text-center px-2">
                    Drop photo or click to upload
                </small>
            </div>
            <input
                ref={fileInputRef}
                type="file"
                accept={acceptedTypes.join(',')}
                onChange={handleFileSelected}
                style={{ display: 'none' }}
                aria-hidden="true"
                tabIndex={-1}
            />
        </div>
    )
}))

ProfilePicUploader.displayName = 'ProfilePicUploader'

export { ProfilePicUploader }
